import { useEffect, useRef } from "react";

import { EvaChart } from "constants/charts.enums";

import { useChartEntities } from "./useChartEntities";
import { extractActiveAndProperties } from "./useChartSessions/utils";

/**
 * Features that are MODES, not settings — never persisted, for any widget.
 *
 * `screenshot` and `fullScreen` describe what a widget is doing right now, not how
 * the user configured it. Saving a dashboard while either is active writes that
 * flag to Mongo and the wrapper faithfully restores it, so a dashboard saved
 * mid-capture reopens stuck in screenshot mode — and the cleanup that clears the
 * flag on the way out only knows a fixed list of widget key prefixes, so nothing
 * downstream would undo it. The record also outlives a revert, because older code
 * reads it too.
 *
 * Same reasoning that keeps the live filter and focus out of the persisted request:
 * live state is re-derived on mount, never frozen into a saved layout.
 *
 * WHY THIS RULE IS NOT UNIVERSAL. The wells charts (useChartSessions) do persist
 * both flags, and that is not an oversight there: `clearScreenshotFromSessionStorage`
 * in ScreenshotContext sweeps them on load, keyed on a fixed list of widget key
 * prefixes. The lean widgets that use THIS hook were never on that list, so nothing
 * would undo a persisted flag for them — which is why they must not write it in the
 * first place. Their prefixes have since been added to that sweep as well, to clean
 * up records written before this rule existed; the two together mean a widget saved
 * mid-capture cannot reopen stuck by either route.
 */
const TRANSIENT_FEATURES = ["screenshot", "fullScreen"] as const;

const pickPersistedFeatures = (features: GenericObject): GenericObject => {
  const persisted = { ...features };
  for (const key of TRANSIENT_FEATURES) delete persisted[key];
  return persisted;
};

const pickRequestKeys = (request: unknown, keys: readonly string[]): GenericObject => {
  if (!request || typeof request !== "object") return {};
  const src = request as GenericObject;
  return keys.reduce((acc, key) => {
    if (src[key] !== undefined) acc[key] = src[key];
    return acc;
  }, {} as GenericObject);
};

/**
 * Persists a lean chart widget's config to sessionStorage under its widget key —
 * the WRITE half of the dashboard round-trip, and the half whose absence silently
 * discards the user's settings.
 *
 * `useSaveDashboard` builds each widget's `settings` from `getSessionData(widget)`,
 * which reads exactly `` `${widget.type}::${widgetId}` ``. With nothing written
 * there it falls through to the widget template's DEFAULT componentState and
 * persists that over whatever the user had chosen — so the "reset" a user sees on
 * reload actually happened at save time.
 *
 * Shared by the rig/frac and Well Status widgets, which differ only in their key
 * and which request fields are worth keeping; the wells charts have their own
 * older equivalent in useChartSessions. Each widget's wrapper seeds its entity from
 * the restored componentState on mount (the read half).
 *
 * @param id chart entity id, which is also the widget id
 * @param keyPrefix MUST equal the widget's `type` in WidgetKeys — the dashboard
 *   reads this key by that string, so a mismatch silently persists nothing
 * @param persistedRequestKeys the user's SELECTIONS. Deliberately a whitelist:
 *   live dashboard state (filter, focus, map viewport) must not be frozen into a
 *   saved layout, or reopening it pins the widget to whatever was active on save
 */
export function useChartWidgetSession(
  id: string,
  keyPrefix: string,
  persistedRequestKeys: readonly string[],
  stamp?: GenericObject
) {
  const { chartType, features, locks, request } = useChartEntities(id);
  const prevRef = useRef<Nullable<string>>(null);
  const stampKey = JSON.stringify(stamp ?? {});

  useEffect(() => {
    // EvaChart.None means the entity does not exist yet -- never persist it.
    if (chartType === EvaChart.None) return;

    const snapshot = {
      chartType,
      features: pickPersistedFeatures(extractActiveAndProperties(features)),
      locks,
      request: pickRequestKeys(request, persistedRequestKeys),
      ...(stamp ?? {})
    };
    const serialized = JSON.stringify(snapshot);
    if (serialized === prevRef.current) return;
    prevRef.current = serialized;
    try {
      sessionStorage.setItem(`${keyPrefix}::${id}`, serialized);
    } catch {
      // best-effort — ignore quota/security errors
    }
    // stampKey, not stamp: callers pass an object literal, which is a new
    // identity every render and would re-run this on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartType,
    features,
    locks,
    request,
    id,
    keyPrefix,
    persistedRequestKeys,
    stampKey
  ]);
}
